'use client';
import React, { useState, useRef, useEffect } from 'react';
import { SUPPORTED_LANGUAGES, type Language } from '@/lib/i18n';

interface LanguageSwitcherProps {
  currentLanguage: Language;
  onLanguageChange: (lang: Language) => void;
}

export default function LanguageSwitcher({ currentLanguage, onLanguageChange }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className="fixed right-4 top-24 z-50"
      style={{ top: '5.5rem' }}
    >
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border shadow-card hover:shadow-card-hover transition-all duration-200 text-sm font-500 text-foreground hover:border-primary/40 group"
      >
        <span className="text-base leading-none">{currentLang.flag}</span>
        <span className="hidden sm:inline text-xs font-600 text-muted-foreground group-hover:text-foreground transition-colors">
          {currentLang.nativeLabel}
        </span>
        <svg
          className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-card border border-border rounded-2xl shadow-card-hover overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-[10px] font-700 text-muted-foreground uppercase tracking-widest">Select Language</p>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  onLanguageChange(lang.code);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors duration-100 ${
                  currentLanguage === lang.code ? 'bg-primary/5 text-primary' : 'text-foreground'
                }`}
              >
                <span className="text-base leading-none flex-shrink-0">{lang.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-600 truncate ${currentLanguage === lang.code ? 'text-primary' : 'text-foreground'}`}>
                    {lang.nativeLabel}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{lang.label}</p>
                </div>
                {currentLanguage === lang.code && (
                  <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
